import { ComponentProps } from 'react';
import {
  PlanCancelSubscriptionOption,
  PlanCancelWizardStep,
  PlanTypes,
} from 'Interfaces/Billing';
import { CancellationReason } from 'Interfaces/UserFeedbacks';

import BillingCancelPlanWizardBeforeYouGo from '../BillingCancelPlanWizardBeforeYouGo';
import BillingCancelPlanWizardDifficultToUse from '../BillingCancelPlanWizardDifficultToUse';
import BillingCancelPlanWizardFinancialReason from '../BillingCancelPlanWizardFinancialReason';
import BillingCancelPlanWizardLoseSubscription from '../BillingCancelPlanWizardLoseSubscription';
import BillingCancelPlanWizardReturnDateForm from '../BillingCancelPlanWizardReturnDateForm';
import BillingCancelPlanWizardYourThoughtsForm from '../BillingCancelPlanWizardYourThoughtsForm';
import { BillingCancelPlanWizardConfirmFormProps } from '../BillingCancelPlanWizardConfirmForm';

type BeforeYouGoConfig = {
  Component: typeof BillingCancelPlanWizardBeforeYouGo;
  getProps: (
    ctx: BillingCancelPlanWizardConfirmFormProps,
  ) => ComponentProps<typeof BillingCancelPlanWizardBeforeYouGo>;
};

type DifficultToUseConfig = {
  Component: typeof BillingCancelPlanWizardDifficultToUse;
  getProps: (
    ctx: BillingCancelPlanWizardConfirmFormProps,
  ) => ComponentProps<typeof BillingCancelPlanWizardDifficultToUse>;
};

type FinancialReasonConfig = {
  Component: typeof BillingCancelPlanWizardFinancialReason;
  getProps: (
    ctx: BillingCancelPlanWizardConfirmFormProps,
  ) => ComponentProps<typeof BillingCancelPlanWizardFinancialReason>;
};

type LoseSubscriptionConfig = {
  Component: typeof BillingCancelPlanWizardLoseSubscription;
  getProps: (
    ctx: BillingCancelPlanWizardConfirmFormProps,
  ) => ComponentProps<typeof BillingCancelPlanWizardLoseSubscription>;
};

type ReturnDateFormConfig = {
  Component: typeof BillingCancelPlanWizardReturnDateForm;
  getProps: (
    ctx: BillingCancelPlanWizardConfirmFormProps,
  ) => ComponentProps<typeof BillingCancelPlanWizardReturnDateForm>;
};

type YourThoughtsConfig = {
  Component: typeof BillingCancelPlanWizardYourThoughtsForm;
  getProps: (
    ctx: BillingCancelPlanWizardConfirmFormProps,
  ) => ComponentProps<typeof BillingCancelPlanWizardYourThoughtsForm>;
};

type WizardComponentConfig =
  | BeforeYouGoConfig
  | DifficultToUseConfig
  | FinancialReasonConfig
  | LoseSubscriptionConfig
  | ReturnDateFormConfig
  | YourThoughtsConfig;

type WizardComponentsByStepAndPlan = Record<
  PlanCancelWizardStep,
  Record<
    PlanCancelSubscriptionOption,
    Omit<
      Record<PlanTypes, Partial<Record<CancellationReason, WizardComponentConfig>>>,
      PlanTypes.FREE
    >
  >
>;

const beforeYouGoConfig: BeforeYouGoConfig = {
  Component: BillingCancelPlanWizardBeforeYouGo,
  getProps: ({ onSecondStep, onSubmitDiscountIncrease }) => ({
    onCancel: onSecondStep,
    onSubmit: onSubmitDiscountIncrease,
  }),
};

const beforeYouGoToTheLastConfig: BeforeYouGoConfig = {
  Component: BillingCancelPlanWizardBeforeYouGo,
  getProps: ({ onLastStep, onSubmitDiscountIncrease }) => ({
    onCancel: onLastStep,
    onSubmit: onSubmitDiscountIncrease,
  }),
};

const difficultToUseConfig: DifficultToUseConfig = {
  Component: BillingCancelPlanWizardDifficultToUse,
  getProps: ({ onLastStep }) => ({
    onSubmit: onLastStep,
  }),
};

const financialReasonConfig: FinancialReasonConfig = {
  Component: BillingCancelPlanWizardFinancialReason,
  getProps: ({ onSecondStep, onSubmitDiscountIncrease }) => ({
    onCancel: onSecondStep,
    onSubmit: onSubmitDiscountIncrease,
  }),
};

const financialReasonToTheLastConfig: FinancialReasonConfig = {
  Component: BillingCancelPlanWizardFinancialReason,
  getProps: ({ onLastStep, onSubmitDiscountIncrease }) => ({
    onCancel: onLastStep,
    onSubmit: onSubmitDiscountIncrease,
  }),
};

const loseSubscriptionConfig: LoseSubscriptionConfig = {
  Component: BillingCancelPlanWizardLoseSubscription,
  getProps: ({ onCancel, isSubmitting, hasValidationErrors }) => ({
    onCancel,
    isSubmitting,
    hasValidationErrors,
  }),
};

const loseSubscriptionFinalConfig: FinancialReasonConfig = {
  Component: BillingCancelPlanWizardLoseSubscription,
  getProps: ({ onCancel, isSubmitting, hasValidationErrors }) => ({
    onCancel,
    isSubmitting,
    hasValidationErrors,
  }),
};

const returnDateFormConfig: ReturnDateFormConfig = {
  Component: BillingCancelPlanWizardReturnDateForm,
  getProps: ({ onLastStep, pristine, isSubmitting, hasValidationErrors }) => ({
    onCancel: onLastStep,
    pristine,
    isSubmitting,
    hasValidationErrors,
  }),
};

const yourThoughtsConfig: YourThoughtsConfig = {
  Component: BillingCancelPlanWizardYourThoughtsForm,
  getProps: ({ onCancel, onLastStep, isSubmitting }) => ({
    onCancel,
    onSubmit: onLastStep,
    isSubmitting,
  }),
};

export const wizardComponentByStepAndPlan: WizardComponentsByStepAndPlan = {
  wizard: {
    trial: {
      [PlanTypes.PERSONAL]: {
        [CancellationReason.PRICING]: beforeYouGoToTheLastConfig,
        [CancellationReason.DIFFICULT_TO_USE]: beforeYouGoConfig,
        [CancellationReason.OTHER]: beforeYouGoConfig,
      },
      [PlanTypes.BUSINESS]: {
        [CancellationReason.PRICING]: beforeYouGoToTheLastConfig,
        [CancellationReason.DIFFICULT_TO_USE]: beforeYouGoConfig,
        [CancellationReason.OTHER]: beforeYouGoConfig,
      },
    },
    monthly: {
      [PlanTypes.PERSONAL]: {
        [CancellationReason.PRICING]: beforeYouGoConfig,
        [CancellationReason.DIFFICULT_TO_USE]: beforeYouGoConfig,
        [CancellationReason.OTHER]: beforeYouGoConfig,
      },
      [PlanTypes.BUSINESS]: {
        [CancellationReason.PRICING]: beforeYouGoToTheLastConfig,
        [CancellationReason.DIFFICULT_TO_USE]: beforeYouGoConfig,
        [CancellationReason.OTHER]: beforeYouGoConfig,
      },
    },
    yearly: {
      [PlanTypes.PERSONAL]: {
        [CancellationReason.FINANCIAL_TROUBLE]: financialReasonToTheLastConfig,
        [CancellationReason.NOT_ENOUGH_USE]: financialReasonConfig,
        [CancellationReason.HAVING_A_BREAK]: financialReasonConfig,
        [CancellationReason.OTHER]: financialReasonConfig,
      },
      [PlanTypes.BUSINESS]: {
        [CancellationReason.FINANCIAL_TROUBLE]: financialReasonToTheLastConfig,
        [CancellationReason.NOT_ENOUGH_USE]: financialReasonConfig,
        [CancellationReason.HAVING_A_BREAK]: financialReasonConfig,
        [CancellationReason.OTHER]: financialReasonConfig,
      },
    },
  },
  hallway: {
    trial: {
      [PlanTypes.PERSONAL]: {
        [CancellationReason.DIFFICULT_TO_USE]: difficultToUseConfig,
        [CancellationReason.OTHER]: yourThoughtsConfig,
      },
      [PlanTypes.BUSINESS]: {
        [CancellationReason.DIFFICULT_TO_USE]: difficultToUseConfig,
        [CancellationReason.OTHER]: yourThoughtsConfig,
      },
    },
    monthly: {
      [PlanTypes.PERSONAL]: {
        [CancellationReason.PRICING]: loseSubscriptionFinalConfig,
        [CancellationReason.DIFFICULT_TO_USE]: difficultToUseConfig,
        [CancellationReason.OTHER]: yourThoughtsConfig,
      },
      [PlanTypes.BUSINESS]: {
        [CancellationReason.DIFFICULT_TO_USE]: difficultToUseConfig,
        [CancellationReason.OTHER]: yourThoughtsConfig,
      },
    },
    yearly: {
      [PlanTypes.PERSONAL]: {
        [CancellationReason.NOT_ENOUGH_USE]: difficultToUseConfig,
        [CancellationReason.HAVING_A_BREAK]: returnDateFormConfig,
        [CancellationReason.OTHER]: yourThoughtsConfig,
      },
      [PlanTypes.BUSINESS]: {
        [CancellationReason.NOT_ENOUGH_USE]: difficultToUseConfig,
        [CancellationReason.HAVING_A_BREAK]: returnDateFormConfig,
        [CancellationReason.OTHER]: yourThoughtsConfig,
      },
    },
  },
  confirm: {
    trial: {
      [PlanTypes.PERSONAL]: {
        [CancellationReason.PRICING]: loseSubscriptionConfig,
        [CancellationReason.DIFFICULT_TO_USE]: loseSubscriptionConfig,
        [CancellationReason.OTHER]: loseSubscriptionConfig,
      },
      [PlanTypes.BUSINESS]: {
        [CancellationReason.PRICING]: loseSubscriptionConfig,
        [CancellationReason.DIFFICULT_TO_USE]: loseSubscriptionConfig,
        [CancellationReason.OTHER]: loseSubscriptionConfig,
      },
    },
    monthly: {
      [PlanTypes.PERSONAL]: {
        [CancellationReason.DIFFICULT_TO_USE]: loseSubscriptionConfig,
        [CancellationReason.OTHER]: loseSubscriptionConfig,
      },
      [PlanTypes.BUSINESS]: {
        [CancellationReason.PRICING]: loseSubscriptionConfig,
        [CancellationReason.DIFFICULT_TO_USE]: loseSubscriptionConfig,
        [CancellationReason.OTHER]: loseSubscriptionConfig,
      },
    },
    yearly: {
      [PlanTypes.PERSONAL]: {
        [CancellationReason.FINANCIAL_TROUBLE]: loseSubscriptionConfig,
        [CancellationReason.NOT_ENOUGH_USE]: loseSubscriptionConfig,
        [CancellationReason.HAVING_A_BREAK]: loseSubscriptionConfig,
        [CancellationReason.OTHER]: loseSubscriptionConfig,
      },
      [PlanTypes.BUSINESS]: {
        [CancellationReason.FINANCIAL_TROUBLE]: loseSubscriptionConfig,
        [CancellationReason.NOT_ENOUGH_USE]: loseSubscriptionConfig,
        [CancellationReason.HAVING_A_BREAK]: loseSubscriptionConfig,
        [CancellationReason.OTHER]: loseSubscriptionConfig,
      },
    },
  },
};
